import React, { useMemo } from 'react';
import { useForm } from '@mantine/form';
import {
    Accordion,
    Button,
    NumberInput,
    SegmentedControl,
    Text,
    Textarea,
    Badge,
} from '@mantine/core';
import { IconMapPin } from '@tabler/icons-react';

import { Request, Store } from '../../codegen/graphql';
import MarginCalculation from './components/MarginCalculation';
import BelowCostApprovalAlert from './BelowCostApprovalAlert';
import { StoreSelectionItem } from './components/StoreSelectionItem';
import { PriceChangeRequestMetadata } from '../../types/price-change-request';
import {
    isPriceBelowCostExVat,
    MAX_SAFE_PRICE,
    toFiniteNumber,
} from '../../helpers/calculateMargin';
import { getCurrencyFromRequest } from '../../utility/getProductCurrency';
import { shouldEnablePnlPriceInput } from '../../helpers/storeHelpers';
import { getHighestCostPrice } from '../../helpers/priceCalculation';
import { getRequestEffectiveCostPrice } from '../../helpers/requestZonePricing';
import {
    filterStoreIdsToEligibleSet,
    getEligibleAdvertisingZoneStores,
    isStoreSelected,
} from '../../helpers/storeSelectionHelper';
import { useHighestCostPriceStore } from '../../hooks/useHighestCostPriceStore';
import { useEligibleStoresCount } from '../../hooks/useEligibleStoresCount';
import { useAppConfig } from '../../hooks/useAppConfig';
import { When } from '../common';
import { PriceChangeFreezeBanner } from './PriceChangeFreezeBanner';
import {
    getPriceChangeScope,
    getSuggestedStoreIds,
    PRICE_CHANGE_SCOPE,
    PriceChangeScopeValue,
    resolveSubmittedPriceChangeScope,
} from '../../helpers/priceChangeScope';

function getStoresForCostPrice(
    scope: PriceChangeScopeValue,
    selectedStoreIds: string[],
    requestStore: Store | null,
    advertisingZoneStores: Store[],
): Store[] {
    if (scope === PRICE_CHANGE_SCOPE.STORE) {
        if (requestStore) {
            return [requestStore];
        }
        return [];
    }

    return advertisingZoneStores.filter(s => selectedStoreIds.includes(s.id));
}

type DmAcceptFormValues = {
    proposedPrice: number | string | undefined;
    notes: string;
    priceChangeScope: PriceChangeScopeValue;
    selectedStores: string[];
};

const SCOPE_OPTIONS: { value: PriceChangeScopeValue; label: string }[] = [
    { value: PRICE_CHANGE_SCOPE.STORE, label: 'Store' },
    { value: PRICE_CHANGE_SCOPE.ZONE, label: 'Zone' },
];

type Props = {
    request: Request & { resolvedVatPercentage?: number | null };
    store: Store | null;
    advertisingZoneStores: Store[];
    close: () => void;
    onConfirm: (input: {
        outcome: string;
        notes: string;
        metadata: PriceChangeRequestMetadata;
    }) => void;
    showPriceChangeFreezeBanner?: boolean;
};

function AcceptPriceChangeRequestFormDm({
    request,
    store,
    advertisingZoneStores,
    close,
    onConfirm,
    showPriceChangeFreezeBanner = false,
}: Props) {
    const { vatPercentage: fallbackVatPercentage } = useAppConfig();
    const resolvedVatPercentage =
        request?.resolvedVatPercentage ?? fallbackVatPercentage;
    const isFromSurvey = Boolean(request?.isFromPriceSurvey);
    const currentScope = getPriceChangeScope(request);
    const suggestedStoreIds = getSuggestedStoreIds(request);
    const highestCostPriceStoreId = useHighestCostPriceStore(
        advertisingZoneStores,
    );
    const totalEligible = useEligibleStoresCount(advertisingZoneStores);
    const eligibleStores = useMemo(
        () => getEligibleAdvertisingZoneStores(advertisingZoneStores),
        [advertisingZoneStores],
    );
    const eligibleStoreIds = useMemo(
        () => eligibleStores.map(s => s.id),
        [eligibleStores],
    );
    const hasAdvertisingZone = eligibleStores.length > 0;

    const initialScope: PriceChangeScopeValue = useMemo(() => {
        if (!hasAdvertisingZone) {
            return PRICE_CHANGE_SCOPE.STORE;
        }
        if (currentScope === PRICE_CHANGE_SCOPE.SPECIFIC_STORES) {
            return PRICE_CHANGE_SCOPE.ZONE;
        }
        if (
            currentScope === PRICE_CHANGE_SCOPE.STORE ||
            currentScope === PRICE_CHANGE_SCOPE.ZONE
        ) {
            return currentScope;
        }
        if (isFromSurvey) {
            return PRICE_CHANGE_SCOPE.ZONE;
        }
        return PRICE_CHANGE_SCOPE.STORE;
    }, [currentScope, hasAdvertisingZone, isFromSurvey]);

    const initialSelectedStores = useMemo(() => {
        const suggestedEligible = filterStoreIdsToEligibleSet(
            suggestedStoreIds,
            eligibleStores,
        );
        if (suggestedEligible.length > 0) {
            return suggestedEligible;
        }
        if (currentScope === PRICE_CHANGE_SCOPE.SPECIFIC_STORES) {
            return [];
        }
        if (initialScope === PRICE_CHANGE_SCOPE.ZONE) {
            return eligibleStoreIds;
        }
        if (store?.id) {
            return filterStoreIdsToEligibleSet([store.id], eligibleStores);
        }
        return eligibleStoreIds;
    }, [
        currentScope,
        eligibleStoreIds,
        eligibleStores,
        initialScope,
        store?.id,
        suggestedStoreIds,
    ]);

    const scopeOptions = useMemo(() => {
        if (!hasAdvertisingZone) {
            return SCOPE_OPTIONS.filter(
                option => option.value === PRICE_CHANGE_SCOPE.STORE,
            );
        }
        return SCOPE_OPTIONS;
    }, [hasAdvertisingZone]);

    const form = useForm<DmAcceptFormValues>({
        initialValues: {
            proposedPrice: request?.proposedPrice ?? undefined,
            notes: '',
            priceChangeScope: initialScope,
            selectedStores: initialSelectedStores,
        },
        validate: values => {
            const needsStores =
                values.priceChangeScope === PRICE_CHANGE_SCOPE.ZONE;
            const proposedPrice = toFiniteNumber(values.proposedPrice);
            const hasValidProposedPrice =
                proposedPrice !== null && proposedPrice > 0;

            return {
                proposedPrice: hasValidProposedPrice
                    ? null
                    : 'Proposed price is required.',
                selectedStores:
                    needsStores && values.selectedStores.length === 0
                        ? 'Select at least one store.'
                        : null,
            };
        },
        validateInputOnBlur: true,
        validateInputOnChange: ['proposedPrice', 'selectedStores'],
    });

    const priceType = useMemo(() => {
        if (store) {
            return shouldEnablePnlPriceInput(store) ? 'pnl' : 'cashbuild';
        }
        return undefined;
    }, [store]);

    const highestCostPrice = useMemo(() => {
        const storesForCost = getStoresForCostPrice(
            form.values.priceChangeScope,
            form.values.selectedStores,
            store,
            advertisingZoneStores,
        );

        return (
            getHighestCostPrice(storesForCost) ??
            getRequestEffectiveCostPrice(request ?? {})
        );
    }, [
        advertisingZoneStores,
        form.values.priceChangeScope,
        form.values.selectedStores,
        request,
        store,
    ]);

    const proposedPriceValue = toFiniteNumber(form.values.proposedPrice);
    const isBelowCost = isPriceBelowCostExVat(
        proposedPriceValue,
        highestCostPrice,
        resolvedVatPercentage,
    );

    const onChangeSelectedStore = React.useCallback(
        (storeId: string, selected: boolean) => {
            const { selectedStores } = form.getValues();
            const nextSelected = selected
                ? selectedStores.concat(storeId)
                : selectedStores.filter(id => id !== storeId);
            form.setFieldValue('selectedStores', nextSelected);
        },
        [form],
    );

    const handleClose = () => {
        form.reset();
        close();
    };

    const handleSubmit = form.onSubmit(values => {
        const proposedPrice = toFiniteNumber(values.proposedPrice);
        if (proposedPrice === null || proposedPrice <= 0) {
            return;
        }

        const suggestedIds =
            values.priceChangeScope === PRICE_CHANGE_SCOPE.STORE
                ? []
                : filterStoreIdsToEligibleSet(
                      values.selectedStores,
                      eligibleStores,
                  );
        const submittedScope = resolveSubmittedPriceChangeScope({
            uiScope: values.priceChangeScope,
            selectedStoreIds: suggestedIds,
            zoneStoreIds: eligibleStoreIds,
        });

        onConfirm({
            outcome: 'Approved',
            notes: values.notes,
            metadata: {
                proposedPrice,
                priceChangeScope: submittedScope,
                suggestedStoreIds: suggestedIds,
            },
        });
        handleClose();
    });

    const productCurrency = useMemo(
        () => getCurrencyFromRequest(request),
        [request],
    );

    const showStoreSelection =
        form.values.priceChangeScope === PRICE_CHANGE_SCOPE.ZONE;

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-5 p-4">
            <div>
                <Text>Adjustment Applicable To</Text>
                <SegmentedControl
                    data={scopeOptions}
                    fullWidth
                    color="primary"
                    classNames={{
                        root: 'mt-1',
                    }}
                    disabled={isFromSurvey || !hasAdvertisingZone}
                    value={form.values.priceChangeScope}
                    onChange={value => {
                        if (
                            value === PRICE_CHANGE_SCOPE.STORE ||
                            value === PRICE_CHANGE_SCOPE.ZONE
                        ) {
                            form.setFieldValue('priceChangeScope', value);
                            if (value === PRICE_CHANGE_SCOPE.ZONE) {
                                form.setFieldValue(
                                    'selectedStores',
                                    eligibleStoreIds,
                                );
                            }
                        }
                    }}
                />
                <When condition={Boolean(form.errors.selectedStores)}>
                    <Text className="mt-2 text-red-500 text-sm">
                        {form.errors.selectedStores}
                    </Text>
                </When>
            </div>
            <When
                condition={
                    form.values.priceChangeScope === PRICE_CHANGE_SCOPE.STORE
                }
            >
                <div className="flex flex-col gap-2">
                    <Text>Applying to Store</Text>
                    <div className="flex gap-2 items-center">
                        <IconMapPin size={18} />
                        <Text className="text-[#4D5E80]">
                            {store?.name || request?.storeName || 'N/A'}
                        </Text>
                        <Badge size="sm" variant="light">
                            {store?.storeNo || request?.storeNo || 'N/A'}
                        </Badge>
                    </div>
                </div>
            </When>
            <When condition={showStoreSelection}>
                <Accordion
                    classNames={{
                        control: 'pl-0 pr-0',
                        item: 'border-none',
                    }}
                >
                    <Accordion.Item value="collapsible">
                        <Accordion.Control>
                            <Text>
                                {form.values.selectedStores.length ===
                                totalEligible
                                    ? `All ${form.values.selectedStores.length}`
                                    : `Selected ${form.values.selectedStores.length}`}{' '}
                                stores in Advertising Zone
                            </Text>
                        </Accordion.Control>
                        <Accordion.Panel>
                            {advertisingZoneStores.map(s => (
                                <StoreSelectionItem
                                    key={s.id}
                                    store={s}
                                    isSelected={isStoreSelected(
                                        s.id,
                                        form.values.selectedStores,
                                    )}
                                    onToggle={onChangeSelectedStore}
                                    isHighestCostPrice={
                                        s.id === highestCostPriceStoreId
                                    }
                                />
                            ))}
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
            </When>
            <NumberInput
                rightSection={
                    <Text className="pr-8 pl-2">{productCurrency}</Text>
                }
                label="Proposed Price"
                decimalScale={2}
                required
                min={0}
                max={MAX_SAFE_PRICE}
                step={0.1}
                {...form.getInputProps('proposedPrice')}
                error={form.errors.proposedPrice}
            />
            <When condition={isBelowCost && !form.errors.proposedPrice}>
                <BelowCostApprovalAlert />
            </When>
            <MarginCalculation
                costPrice={highestCostPrice}
                currentSellingPrice={request?.currentPrice ?? 0}
                proposedPrice={proposedPriceValue ?? 0}
                benchmarkMargin={request?.benchmarkMargin ?? 0}
                currency={productCurrency}
                completed={request?.completed}
                previousCostPrice={request?.previousCostPrice}
                previousSellingPrice={request?.previousSellingPrice}
                previousCashbuildPrice={request?.previousCashbuildPrice}
                previousPnlPrice={request?.previousPnlPrice}
                priceType={priceType}
                layout="row"
                vatPercentage={resolvedVatPercentage}
            />
            <When condition={showPriceChangeFreezeBanner}>
                <PriceChangeFreezeBanner />
            </When>
            <Textarea
                size="md"
                radius="lg"
                label="Notes"
                placeholder="Additional Notes"
                classNames={{
                    label: 'text-[16px]',
                }}
                {...form.getInputProps('notes')}
            />
            <div className="flex gap-4">
                <Button size="md" variant="outline" onClick={handleClose}>
                    Cancel
                </Button>
                <Button size="md" type="submit">
                    Proceed
                </Button>
            </div>
        </form>
    );
}

export default AcceptPriceChangeRequestFormDm;
